import {Button, Icon, Typography} from '@ezcater/tapas';
import {faChevronRight} from '@fortawesome/pro-regular-svg-icons';
import router from 'next/router';
import {twJoin} from 'tailwind-merge';

import {type CuisineTaxonEnum} from '@/components/features/search/filters/FiltersUtils/cuisines';
import {DietaryOptionEnum, SearchServiceCapabilitiesTaxonomy} from '@/graphql/types';
import useTracking from '@/hooks/useTracking';
import {SEARCH_NEW_PATH} from '@/paths';
import {compilePath} from '@/utils';

export type QuickFilterButtonOption = {
  hideOnDesktop?: boolean;
  id: string;
  label: React.ReactNode;
  onClick?: () => void;
  prefix: React.ReactNode;
  filterParams?: {
    cuisineOption?: CuisineTaxonEnum;
    'dietaryOptions[]'?: DietaryOptionEnum[];
    individualPackaging?: boolean;
    serviceCapabilitiesTaxonomy?: SearchServiceCapabilitiesTaxonomy;
  };
};

type QuickFilterButtonProps = {
  addressString?: string;
  button: QuickFilterButtonOption;
  useNewBreakpoint?: boolean;
};

const QuickFilterButton: React.FC<QuickFilterButtonProps> = ({
  addressString = '',
  button,
  useNewBreakpoint = false,
}) => {
  const {trackClick} = useTracking();

  const handleOnClick = async () => {
    trackClick(`${button.id}-quick-filter-button`, {
      misc_json: JSON.stringify({addressString}),
    });

    button.onClick?.();

    const addressParams = {addressString};
    router.push(
      compilePath(
        SEARCH_NEW_PATH,
        {},
        {...addressParams, ...button.filterParams},
        {skipEmptyString: true, skipNull: true},
      ),
    );
  };

  const desktopBreakpoint = useNewBreakpoint ? 'min-[1036px]:flex' : 'desktop924:flex';

  return (
    <>
      <Button
        className={twJoin(
          'shrink-0 gap-2 border-[#00e15d] bg-green-700 p-1 pr-4 font-bold text-white hover:bg-[#00592d] focus:bg-ezgreen-800 active:bg-ezgreen-800 max-[639px]:hidden',
          // The "More" filter only appears in the mobile grid.
          button.hideOnDesktop && 'min-[640px]:hidden',
          !button.hideOnDesktop && desktopBreakpoint,
        )}
        data-testid={button.hideOnDesktop ? undefined : 'quick-filter-buttons-desktop'}
        onClick={handleOnClick}
        size="medium"
        variant="outlined"
      >
        {button.prefix}
        <Typography className="whitespace-nowrap pl-1 empty:hidden" variant="body-sm">
          {button.label}
        </Typography>
        <Icon icon={faChevronRight} className="h-3" />
      </Button>

      <Button
        className="relative h-full w-full min-w-0 rounded-xl border-[1.5px] border-green-300 bg-green-700 px-2 py-3 font-bold text-white hover:bg-[#00592d] focus:bg-ezgreen-800 active:bg-ezgreen-800 min-[640px]:hidden"
        data-testid="quick-filter-buttons"
        onClick={handleOnClick}
        size="medium"
        variant="outlined"
      >
        <div className="flex flex-col items-center justify-center gap-1">
          {button.prefix}
          <Typography
            className="whitespace-nowrap text-center leading-5 empty:hidden"
            variant="body-sm"
          >
            {button.label}
          </Typography>
        </div>
      </Button>
    </>
  );
};

export default QuickFilterButton;
